/**
 * TCPA SMS-consent copy — the platform-owned contract shared by every block with a phone capture
 * (docs/SMS_CONSENT.md, version `v1-2026-09`).
 *
 * Why this module exists: the consent record we keep for TCPA stores a VERSION ID, not the words
 * (`leads.consent_text_version` / `site_visitors.consent_text_version`). That is only evidence if
 * the id provably identifies the wording the visitor actually saw. An agent can configure a
 * block's `consentText`, and an agent cannot mint a platform version id — so custom wording has
 * no version and must never be recorded as `v1-2026-09`.
 *
 * The contract, one design for all the capture blocks:
 *   1. The copy is platform-owned. A block renders the configured `consentText` ONLY when it is
 *      one of the approved wordings below (`approvedSmsConsentText()`); anything else falls back
 *      to the block's approved default, so the visitor never sees unversioned compliance copy.
 *   2. The submission carries the words that were rendered (`consent_text`), never a version id.
 *      The gateway derives the version from those words (services/api-gateway/src/lib/sms-consent.ts)
 *      and refuses to record consent for wording it does not recognise. Same discipline as the
 *      fixed outbound SMS copy in the gateway's lib/speed-to-lead.ts.
 *
 * These literals MUST stay identical to `APPROVED_SMS_CONSENT_TEXTS` in
 * services/api-gateway/src/lib/sms-consent.ts (this package imports nothing from the gateway).
 * Changing any wording means bumping `SMS_CONSENT_TEXT_VERSION` in both places and in
 * docs/SMS_CONSENT.md, together, in one change.
 *
 * Framework-agnostic and server-renderable: plain strings and pure functions, no browser or
 * Next-only APIs, no runtime dependencies.
 */

/** Version id of the approved wordings below. Bump on ANY wording change (docs/SMS_CONSENT.md). */
export const SMS_CONSENT_TEXT_VERSION = 'v1-2026-09';

/** Home-value capture (HomeValuation). Mirrors the gateway's SMS_CONSENT_CHECKBOX_TEXT. */
export const VALUATION_SMS_CONSENT_TEXT =
  'Yes, text me about my home value. By checking this box I agree to receive automated text messages from this agent at the number I provided. Consent is not a condition of purchase. Msg frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.';

/** Seller calculators (NetProceeds, SellVsRent — the shared PdfGate). */
export const CALC_SMS_CONSENT_TEXT =
  'Yes, text me about my numbers. By checking this box I agree to receive automated text messages from this agent at the number I provided. Consent is not a condition of purchase. Msg frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.';

/** Community-match quiz (CommunityMatch). */
export const MATCH_SMS_CONSENT_TEXT =
  'Yes, text me about my matches. By checking this box I agree to receive automated text messages from this agent at the number I provided. Consent is not a condition of purchase. Msg frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.';

/** Every wording covered by SMS_CONSENT_TEXT_VERSION. Nothing else may be shown or recorded. */
export const APPROVED_SMS_CONSENT_TEXTS: readonly string[] = [
  VALUATION_SMS_CONSENT_TEXT,
  CALC_SMS_CONSENT_TEXT,
  MATCH_SMS_CONSENT_TEXT,
];

/** Whitespace-insensitive comparison key — a re-wrapped copy of the approved copy is the same copy. */
export function normalizeConsentText(text: string | null | undefined): string {
  return String(text ?? '').replace(/\s+/g, ' ').trim();
}

/** True when `text` is one of the approved wordings for SMS_CONSENT_TEXT_VERSION. */
export function isApprovedSmsConsentText(text: string | null | undefined): boolean {
  const normalized = normalizeConsentText(text);
  if (!normalized) return false;
  return APPROVED_SMS_CONSENT_TEXTS.some((approved) => normalizeConsentText(approved) === normalized);
}

/**
 * The copy a block renders next to its consent checkbox: the agent's configured wording when it
 * is approved, otherwise the block's approved default. Agent-edited compliance copy is never
 * shown, because it could not be recorded honestly.
 */
export function approvedSmsConsentText(configured: string | null | undefined, fallback: string): string {
  const text = typeof configured === 'string' ? configured.trim() : '';
  return text && isApprovedSmsConsentText(text) ? text : fallback;
}

/**
 * The consent fields for a capture POST. Consent is only ever asserted WITH a phone number
 * (without one there is nothing to consent to) and always travels with the exact words that were
 * rendered, so the gateway — not the client — decides the stored version id.
 */
export function smsConsentFields(input: { checked: boolean; hasPhone: boolean; shownText: string }): {
  consent_sms: boolean;
  consent_text?: string;
} {
  const consent = input.checked === true && input.hasPhone === true;
  return consent ? { consent_sms: true, consent_text: input.shownText } : { consent_sms: false };
}
